import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Doughnut, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title, CategoryScale, LinearScale, BarElement } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { handleAuthError, trackerFetch } from '../utils/auth';

ChartJS.register(ArcElement, Tooltip, Legend, Title, ChartDataLabels, CategoryScale, LinearScale, BarElement);

const mistakeLabelMap = {
    ace: 'Aces',
    winner: 'Winners',
    firstServeFault: '1st Serve Faults',
    first_serve_fault: '1st Serve Faults',
    doubleFault: 'Double Faults',
    double_fault: 'Double Faults',
    unforcedError: 'Unforced Errors',
    unforced_error: 'Unforced Errors',
    forcedError: 'Forced Errors',
    forced_error: 'Forced Errors',
    poorFootwork: 'Poor Footwork',
    poor_footwork: 'Poor Footwork',
    tacticalMistake: 'Tactical Mistake',
    tactical_mistake: 'Tactical Mistake',
};

const formatMistakeType = (k) => mistakeLabelMap[k] || k.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim();

const mistakeTypesList = [
    { key: 'ace', label: 'Aces', alts: ['Ace', 'ace'] },
    { key: 'winner', label: 'Winners', alts: ['Winner', 'winner'] },
    { key: 'firstServeFault', label: '1st Serve Faults', alts: ['First Serve Fault', 'First Server Fault', 'first_serve_fault'] },
    { key: 'doubleFault', label: 'Double Faults', alts: ['Double Fault', 'double_fault'] },
    { key: 'unforcedError', label: 'Unforced Errors', alts: ['Unforced Error', 'unforced_error'] },
    { key: 'forcedError', label: 'Forced Errors', alts: ['Forced Error', 'forced_error'] },
    { key: 'poorFootwork', label: 'Poor Footwork', alts: ['Poor Footwork', 'poor_footwork'] },
    { key: 'tacticalMistake', label: 'Tactical Mistake', alts: ['Tactical Mistake', 'tactical_mistake'] },
];

const MatchDetailPage = () => {
    const { matchId } = useParams();
    const navigate = useNavigate();
    const [matchData, setMatchData] = useState(null);
    const [currentState, setCurrentState] = useState(null);
    const [selectedFilter, setSelectedFilter] = useState('wholeMatch');
    const [mistakesByPlayerChartData, setMistakesByPlayerChartData] = useState({});
    const [mistakesByTypeChartData, setMistakesByTypeChartData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Accordion state for expandable sets and games
    const [expandedSets, setExpandedSets] = useState({ 0: true });
    const [expandedGames, setExpandedGames] = useState({});

    const toggleSetExpansion = (setIndex) => {
        setExpandedSets(prev => ({
            ...prev,
            [setIndex]: !prev[setIndex]
        }));
    };

    const toggleGameExpansion = (gameKey) => {
        setExpandedGames(prev => ({
            ...prev,
            [gameKey]: !prev[gameKey]
        }));
    };

    useEffect(() => {
        const fetchMatchDetails = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    handleAuthError(navigate);
                    return;
                }

                const res = await trackerFetch(`get_match_details.php?match_id=${matchId}`);
                if (res && res.data && res.data.success && res.data.match) {
                    setMatchData(res.data.match);
                    let cs = res.data.current_state || res.data.match?.current_state;
                    if (typeof cs === 'string') {
                        try {
                            cs = JSON.parse(cs);
                        } catch (e) {
                            cs = null;
                        }
                    }
                    setCurrentState(cs);
                } else {
                    setError(res?.data?.message || 'Match not found');
                }
            } catch (err) {
                if (err.message && err.message.includes('401')) {
                    handleAuthError(navigate);
                } else {
                    setError(err.message);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchMatchDetails();
    }, [matchId, navigate]);

    useEffect(() => {
        if (matchData) {
            const processChartData = () => {
                const player1Name = matchData.player1_name || 'Player 1';
                const player2Name = matchData.player2_name || matchData.opponent_name || matchData.opponent || 'Player 2';

                let player1TotalMistakes = 0;
                let player2TotalMistakes = 0;
                let player1MistakeDetails = {};
                let player2MistakeDetails = {};

                const standardMistakeTypes = ['ace', 'winner', 'firstServeFault', 'doubleFault', 'unforcedError', 'forcedError'];

                if (selectedFilter === 'wholeMatch') {
                    player1TotalMistakes = matchData.player1_total_mistakes || 0;
                    player2TotalMistakes = matchData.player2_total_mistakes || 0;
                    player1MistakeDetails = { ...(matchData.player1_total_mistakes_details || {}) };
                    player2MistakeDetails = { ...(matchData.player2_total_mistakes_details || {}) };

                    // If totals are 0, aggregate from sets & games
                    if (Object.keys(player1MistakeDetails).length === 0 && Object.keys(player2MistakeDetails).length === 0 && Array.isArray(matchData.sets)) {
                        matchData.sets.forEach(set => {
                            if (Array.isArray(set.games)) {
                                set.games.forEach(g => {
                                    const p1 = g.mistake_details?.player1_mistakes_details || g.myMistakes || g.player1_mistakes_details || {};
                                    const p2 = g.mistake_details?.player2_mistakes_details || g.opponentMistakes || g.player2_mistakes_details || {};
                                    Object.keys(p1).forEach(k => {
                                        const v = parseInt(p1[k], 10) || 0;
                                        if (v > 0) {
                                            player1MistakeDetails[k] = (player1MistakeDetails[k] || 0) + v;
                                            player1TotalMistakes += v;
                                        }
                                    });
                                    Object.keys(p2).forEach(k => {
                                        const v = parseInt(p2[k], 10) || 0;
                                        if (v > 0) {
                                            player2MistakeDetails[k] = (player2MistakeDetails[k] || 0) + v;
                                            player2TotalMistakes += v;
                                        }
                                    });
                                });
                            }
                        });
                    }
                } else if (selectedFilter.startsWith('set')) {
                    const setIndex = parseInt(selectedFilter.replace('set', ''), 10) - 1;
                    const set = matchData.sets?.[setIndex];
                    if (set) {
                        player1TotalMistakes = set.player1_mistakes || 0;
                        player2TotalMistakes = set.player2_mistakes || 0;
                        player1MistakeDetails = { ...(set.player1_mistakes_details || {}) };
                        player2MistakeDetails = { ...(set.player2_mistakes_details || {}) };

                        if (Object.keys(player1MistakeDetails).length === 0 && Object.keys(player2MistakeDetails).length === 0 && Array.isArray(set.games)) {
                            set.games.forEach(g => {
                                const p1 = g.mistake_details?.player1_mistakes_details || g.myMistakes || g.player1_mistakes_details || {};
                                const p2 = g.mistake_details?.player2_mistakes_details || g.opponentMistakes || g.player2_mistakes_details || {};
                                Object.keys(p1).forEach(k => {
                                    const v = parseInt(p1[k], 10) || 0;
                                    if (v > 0) {
                                        player1MistakeDetails[k] = (player1MistakeDetails[k] || 0) + v;
                                        player1TotalMistakes += v;
                                    }
                                });
                                Object.keys(p2).forEach(k => {
                                    const v = parseInt(p2[k], 10) || 0;
                                    if (v > 0) {
                                        player2MistakeDetails[k] = (player2MistakeDetails[k] || 0) + v;
                                        player2TotalMistakes += v;
                                    }
                                });
                            });
                        }
                    }
                } else if (selectedFilter.startsWith('game')) {
                    const gameIdentifier = selectedFilter.replace('game', '');
                    const [targetSetIndex, targetGameIndex] = gameIdentifier.split('-').map(Number);

                    const set = matchData.sets?.[targetSetIndex];
                    if (set && set.games?.[targetGameIndex]) {
                        const game = set.games[targetGameIndex];
                        const p1 = game.mistake_details?.player1_mistakes_details || game.myMistakes || game.player1_mistakes_details || {};
                        const p2 = game.mistake_details?.player2_mistakes_details || game.opponentMistakes || game.player2_mistakes_details || {};
                        
                        player1MistakeDetails = { ...p1 };
                        player2MistakeDetails = { ...p2 };

                        player1TotalMistakes = Object.values(player1MistakeDetails).reduce((a, b) => a + (parseInt(b, 10) || 0), 0);
                        player2TotalMistakes = Object.values(player2MistakeDetails).reduce((a, b) => a + (parseInt(b, 10) || 0), 0);
                    }
                }

                setMistakesByPlayerChartData({
                    labels: [player1Name, player2Name],
                    datasets: [
                        {
                            label: 'Total Mistakes',
                            data: [player1TotalMistakes, player2TotalMistakes],
                            backgroundColor: [
                                'rgba(75, 192, 192, 0.6)',
                                'rgba(255, 99, 132, 0.6)',
                            ],
                            borderColor: [
                                'rgba(75, 192, 192, 1)',
                                'rgba(255, 99, 132, 1)',
                            ],
                            borderWidth: 1,
                        },
                    ],
                });

                const dynamicTypes = [
                    ...Object.keys(player1MistakeDetails),
                    ...Object.keys(player2MistakeDetails)
                ];
                const allMistakeTypes = [...new Set([...standardMistakeTypes, ...dynamicTypes])];

                setMistakesByTypeChartData({
                    labels: allMistakeTypes.map(formatMistakeType),
                    datasets: [
                        {
                            label: player1Name,
                            data: allMistakeTypes.map(type => player1MistakeDetails[type] || 0),
                            backgroundColor: 'rgba(75, 192, 192, 0.6)',
                            borderColor: 'rgba(75, 192, 192, 1)',
                            borderWidth: 1,
                        },
                        {
                            label: player2Name,
                            data: allMistakeTypes.map(type => player2MistakeDetails[type] || 0),
                            backgroundColor: 'rgba(255, 99, 132, 0.6)',
                            borderColor: 'rgba(255, 99, 132, 1)',
                            borderWidth: 1,
                        },
                    ],
                });
            };
            processChartData();
        }
    }, [matchData, selectedFilter]);

    const mistakesByPlayerChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: { font: { size: 14 } },
            },
            title: {
                display: true,
                text: 'Mistakes by Player',
                font: { size: 18 },
            },
            datalabels: {
                color: '#fff',
                formatter: (value, context) => {
                    const total = context.chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
                    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) + '%' : '0%';
                    return `${value} (${percentage})`;
                },
                font: { weight: 'bold', size: 14 },
            },
        },
    };

    const mistakesByTypeChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: { font: { size: 14 } },
            },
            title: {
                display: true,
                text: 'Mistakes by Type and Player',
                font: { size: 18 },
            },
            datalabels: {
                color: '#000',
                anchor: 'center',
                align: 'center',
                formatter: (value) => value > 0 ? value : '',
                font: { weight: 'bold', size: 12 },
            },
        },
        scales: {
            x: {
                stacked: false,
                title: { display: true, text: 'Mistake Type', font: { size: 14 } },
            },
            y: {
                stacked: false,
                beginAtZero: true,
                title: { display: true, text: 'Number of Mistakes', font: { size: 14 } },
            },
        },
    };

    const handleReopenMatch = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                handleAuthError(navigate);
                return;
            }

            await fetch('https://best3intennis.com/best3intennis-api/api/reopen_match.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ matchId, match_id: matchId })
            });

            navigate(`/match-tracker/${matchId}`, { state: { isContinuedMatch: true } });
        } catch (e) {
            console.error('Error reopening match:', e);
            navigate(`/match-tracker/${matchId}`, { state: { isContinuedMatch: true } });
        }
    };

    const getFormattedScore = (match, cs) => {
        if (!match) return '0-0';
        let scoreText = '';
        let matchScores = [];

        if (match.match_scores_json) {
            try {
                matchScores = typeof match.match_scores_json === 'string' ? JSON.parse(match.match_scores_json) : match.match_scores_json;
            } catch (e) {}
        }

        if (!matchScores.length && cs?.matchScores) {
            matchScores = cs.matchScores;
        }

        if (Array.isArray(matchScores) && matchScores.length > 0) {
            scoreText = matchScores.map(s => {
                const tb = s?.tbScore ? ` (${s.tbScore})` : '';
                return `${s?.myGames || s?.player1_games || 0}-${s?.opponentGames || s?.player2_games || 0}${tb}`;
            }).join(', ');
        } else if (match.score && match.score !== '0-0') {
            scoreText = match.score;
        }

        const isMatchDone = String(match.status || '').toLowerCase() === 'completed' || cs?.matchCompleted === true || (Boolean(match.winner) && String(match.winner).toLowerCase() !== 'inprogress' && String(match.winner).toLowerCase() !== 'in_progress');
        const isInProg = !isMatchDone;
        if (isInProg) {
            if (cs && ((cs.myGamesWonInSet || 0) > 0 || (cs.opponentGamesWonInSet || 0) > 0)) {
                const activeScore = `${cs.myGamesWonInSet || 0}-${cs.opponentGamesWonInSet || 0}`;
                const parts = scoreText ? scoreText.split(',').map(s => s.trim()).filter(Boolean) : [];
                const setNum = parseInt(cs.setNumber, 10) || 1;

                if (setNum > parts.length) {
                    parts.push(activeScore);
                } else if (setNum === parts.length) {
                    parts[parts.length - 1] = activeScore;
                }
                scoreText = parts.join(', ');
            }
        }

        return scoreText || match.score || '0-0';
    };

    const getGameMistakesList = (game) => {
        if (!game) return [];
        const p1 = game.mistake_details?.player1_mistakes_details || game.myMistakes || game.player1_mistakes_details || {};
        const p2 = game.mistake_details?.player2_mistakes_details || game.opponentMistakes || game.player2_mistakes_details || {};

        const list = [];
        mistakeTypesList.forEach(m => {
            let p1Count = 0;
            let p2Count = 0;
            const keys = [m.key, ...(m.alts || [])];
            for (const k of keys) {
                if (p1[k] !== undefined && p1[k] !== null) p1Count += parseInt(p1[k], 10) || 0;
                if (p2[k] !== undefined && p2[k] !== null) p2Count += parseInt(p2[k], 10) || 0;
            }
            if (p1Count > 0 || p2Count > 0) {
                list.push({ label: m.label, me: p1Count, opponent: p2Count });
            }
        });
        return list;
    };

    if (loading) {
        return <div className="text-center py-12 text-gray-600">Loading match details...</div>;
    }

    if (error) {
        return (
            <div className="container mx-auto p-6 max-w-md text-center">
                <div className="bg-white rounded-xl shadow-md border border-gray-100 p-8 my-12">
                    <div className="text-4xl mb-3">⚠️</div>
                    <h2 className="text-lg font-bold text-gray-800 mb-2">Failed to load match</h2>
                    <p className="text-sm text-gray-500 mb-6">{error}</p>
                    <div className="flex flex-col sm:flex-row justify-center gap-3">
                        <button
                            onClick={() => window.location.reload()}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-5 rounded-lg shadow-sm transition"
                        >
                            🔄 Retry
                        </button>
                        <button
                            onClick={() => navigate('/history')}
                            className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 px-5 rounded-lg transition"
                        >
                            ← Go to History
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!matchData) {
        return <div className="text-center py-12 text-gray-600">No match data found.</div>;
    }

    const statusStr = String(matchData.status || '').toLowerCase();
    const isInProgress = (statusStr === 'inprogress' || statusStr === 'in_progress') && (!currentState || currentState.matchCompleted !== true);
    const winnerStr = String(matchData.result || matchData.winner || '').toLowerCase();
    const isWon = winnerStr === 'me' || winnerStr === 'player1' || winnerStr === 'won' || matchData.winner_id === matchData.player1_id;

    const player1Name = matchData.player1_name || 'Player 1';
    const player2Name = matchData.player2_name || matchData.opponent_name || matchData.opponent || 'Player 2';
    const displayScore = getFormattedScore(matchData, currentState);

    return (
        <div className="container mx-auto p-4 max-w-5xl">
            {/* Header Card matching mobile behavior */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-6 border border-gray-100">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900">
                                {player1Name} vs {player2Name}
                            </h1>
                            {isInProgress ? (
                                <span className="px-3 py-1 rounded-full text-xs font-bold text-white bg-blue-600">
                                    In Progress
                                </span>
                            ) : (
                                <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${isWon ? 'bg-green-500' : 'bg-red-500'}`}>
                                    {isWon ? 'Won' : 'Lost'}
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-gray-500">
                            {new Date(matchData.match_date || matchData.created_at || Date.now()).toLocaleDateString()} • {matchData.surface || matchData.location || 'Tennis Match'}
                        </p>
                        <div className="text-xl font-bold text-gray-800 mt-2">
                            Score: <span className="text-blue-600">{displayScore}</span>
                        </div>
                    </div>

                    <div className="w-full sm:w-auto flex flex-col sm:flex-row gap-2">
                        {isInProgress ? (
                            <button
                                onClick={() => navigate(`/match-tracker/${matchId}`, { state: { isContinuedMatch: true } })}
                                className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg shadow-md transition duration-200 flex items-center justify-center gap-2 text-base"
                            >
                                ▶️ Resume / Continue Match
                            </button>
                        ) : (
                            <button
                                onClick={handleReopenMatch}
                                className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-lg shadow-md transition duration-200 flex items-center justify-center gap-2 text-base"
                            >
                                🔓 Re-open & Continue / Edit Match
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Filter Section */}
            <div className="mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                <label htmlFor="filter-dropdown" className="block text-base font-semibold text-gray-800 mb-2">View Mistakes By:</label>
                <select
                    id="filter-dropdown"
                    className="block w-full pl-3 pr-10 py-2.5 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md bg-gray-50 font-medium"
                    value={selectedFilter}
                    onChange={(e) => setSelectedFilter(e.target.value)}
                >
                    <option value="wholeMatch">Whole Match</option>
                    {matchData.sets && matchData.sets.map((set, setIndex) => (
                        <React.Fragment key={`set-fragment-${setIndex}`}>
                            <option value={`set${setIndex + 1}`}>Set {setIndex + 1}</option>
                            {set.games && set.games.map((game, gameIndex) => (
                                <option key={`game-${setIndex}-${gameIndex}`} value={`game${setIndex}-${gameIndex}`}>
                                    &nbsp;&nbsp;&nbsp;Game {gameIndex + 1} (Set {setIndex + 1})
                                </option>
                            ))}
                        </React.Fragment>
                    ))}
                </select>
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100" style={{ height: '400px' }}>
                    {mistakesByPlayerChartData.labels && mistakesByPlayerChartData.labels.length > 0 && (mistakesByPlayerChartData.datasets[0].data[0] > 0 || mistakesByPlayerChartData.datasets[0].data[1] > 0) ? (
                        <Doughnut data={mistakesByPlayerChartData} options={mistakesByPlayerChartOptions} />
                    ) : (
                        <div className="h-full flex items-center justify-center text-gray-500 font-medium">
                            No mistake data recorded for this selection.
                        </div>
                    )}
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100" style={{ height: '400px' }}>
                    {mistakesByTypeChartData.labels && mistakesByTypeChartData.labels.length > 0 && (mistakesByTypeChartData.datasets[0].data.some(val => val > 0) || mistakesByTypeChartData.datasets[1].data.some(val => val > 0)) ? (
                        <Bar data={mistakesByTypeChartData} options={mistakesByTypeChartOptions} />
                    ) : (
                        <div className="h-full flex items-center justify-center text-gray-500 font-medium">
                            No mistake breakdown available for this selection.
                        </div>
                    )}
                </div>
            </div>

            {/* Detailed Sets & Games Log with expandable games */}
            <div className="mt-8 bg-white p-6 rounded-xl shadow-md border border-gray-100">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold text-gray-900">Detailed Sets & Games Log</h2>
                    <span className="text-xs text-gray-500 italic">Click on any game to view mistake details</span>
                </div>

                {matchData.sets && matchData.sets.length > 0 ? (
                    matchData.sets.map((set, setIndex) => {
                        const isSetExpanded = expandedSets[setIndex] !== false; // Default true
                        const setScore = set.score || `${set.player1_games || 0}-${set.player2_games || 0}`;

                        return (
                            <div key={setIndex} className="border border-gray-200 rounded-xl mb-5 overflow-hidden bg-white shadow-sm">
                                {/* Set Header Accordion */}
                                <div
                                    onClick={() => toggleSetExpansion(setIndex)}
                                    className="flex justify-between items-center p-4 bg-gray-100 hover:bg-gray-200/80 cursor-pointer transition select-none"
                                >
                                    <div>
                                        <h3 className="font-extrabold text-lg text-gray-800">
                                            Set {setIndex + 1}: <span className="text-blue-600 ml-1">{setScore}</span>
                                        </h3>
                                        <div className="flex gap-4 text-xs font-semibold text-gray-600 mt-1">
                                            <span>{player1Name} Mistakes: <strong className="text-gray-900">{set.player1_mistakes || 0}</strong></span>
                                            <span>•</span>
                                            <span>{player2Name} Mistakes: <strong className="text-gray-900">{set.player2_mistakes || 0}</strong></span>
                                        </div>
                                    </div>
                                    <div className="text-gray-600 font-bold text-sm flex items-center gap-1">
                                        <span>{isSetExpanded ? 'Collapse' : 'Expand'}</span>
                                        <span>{isSetExpanded ? '▲' : '▼'}</span>
                                    </div>
                                </div>

                                {/* Games Container */}
                                {isSetExpanded && (
                                    <div className="p-4 bg-gray-50 space-y-3">
                                        <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                                            Games in Set {setIndex + 1}
                                        </div>

                                        {set.games && set.games.length > 0 ? (
                                            set.games.map((game, gameIndex) => {
                                                const gameKey = `${setIndex}_${game.game_id || game.game_number || gameIndex}`;
                                                const isGameExpanded = !!expandedGames[gameKey];
                                                const isMeWinner = game.winner === 'me' || game.winner === 'player1';
                                                const isOppWinner = game.winner === 'opponent' || game.winner === 'player2';
                                                const winnerName = isMeWinner ? player1Name : (isOppWinner ? player2Name : 'Completed');
                                                const gameMistakes = getGameMistakesList(game);
                                                const gameScoreText = game.point_score || game.pointScore || game.score || '';

                                                let serverLabel = '';
                                                if (game.serving_player) {
                                                    const s = String(game.serving_player).toLowerCase();
                                                    serverLabel = (s === 'me' || s === 'player1') ? `${player1Name} serving` : `${player2Name} serving`;
                                                }

                                                return (
                                                    <div
                                                        key={gameKey}
                                                        className={`border rounded-lg transition overflow-hidden ${isGameExpanded ? 'border-blue-300 bg-white shadow-md' : 'border-gray-200 bg-white hover:border-blue-200 shadow-sm'}`}
                                                    >
                                                        {/* Clickable Game Summary Row */}
                                                        <div
                                                            onClick={() => toggleGameExpansion(gameKey)}
                                                            className="p-3.5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 cursor-pointer select-none"
                                                        >
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span className="font-bold text-gray-900 text-base">
                                                                    Game {game.game_number || gameIndex + 1}
                                                                </span>

                                                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${isMeWinner ? 'bg-green-100 text-green-800' : (isOppWinner ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800')}`}>
                                                                    Winner: {winnerName}
                                                                </span>

                                                                {serverLabel && (
                                                                    <span className="bg-yellow-50 text-yellow-800 border border-yellow-200 px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1">
                                                                        🎾 {serverLabel}
                                                                    </span>
                                                                )}

                                                                {gameScoreText && (
                                                                    <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-xs font-mono font-semibold">
                                                                        {gameScoreText}
                                                                    </span>
                                                                )}
                                                            </div>

                                                            <div className="flex items-center gap-2 text-xs font-semibold text-blue-600 self-end sm:self-center">
                                                                <span>{isGameExpanded ? 'Hide Details' : 'View Mistakes'}</span>
                                                                <span className="text-gray-500">{isGameExpanded ? '▲' : '▼'}</span>
                                                            </div>
                                                        </div>

                                                        {/* Expanded Game Mistakes Breakdown */}
                                                        {isGameExpanded && (
                                                            <div className="border-t border-gray-100 bg-blue-50/40 p-4">
                                                                <h5 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3">
                                                                    Game Mistakes Breakdown
                                                                </h5>

                                                                {gameMistakes.length > 0 ? (
                                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                                        {gameMistakes.map((gm, i) => (
                                                                            <div key={i} className="flex justify-between items-center bg-white p-2.5 rounded-md border border-blue-100 shadow-xs">
                                                                                <span className="text-sm font-medium text-gray-800">{gm.label}</span>
                                                                                <div className="flex items-center gap-2 text-xs font-bold">
                                                                                    <span className="text-green-700 bg-green-50 px-2 py-0.5 rounded border border-green-200">
                                                                                        {player1Name}: {gm.me}
                                                                                    </span>
                                                                                    <span className="text-red-700 bg-red-50 px-2 py-0.5 rounded border border-red-200">
                                                                                        {player2Name}: {gm.opponent}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                ) : (
                                                                    <div className="text-sm text-gray-600 bg-white p-3 rounded-md border border-gray-200 flex items-center gap-2">
                                                                        <span>✨</span>
                                                                        <span>No mistakes recorded in this game (Clean Game).</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <p className="text-gray-500 text-sm italic">No games recorded in this set.</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })
                ) : (
                    <p className="text-gray-500">No completed sets recorded for this match yet.</p>
                )}
            </div>
        </div>
    );
};

export default MatchDetailPage;
